import React, { useCallback, useMemo } from 'react';
import { CardTemplate } from '../../shared/CardTemplate';
import { FormattedMessage } from 'react-intl';
import { Color, Column, Row, Text, TextLink, useTelemetry } from '@snowflake/core-ui';
import { SignupCompleteHeader } from './SignupCompleteHeader';
import { useSignupFormController } from './SignupFormControllerContext';
import { SignupCardButton } from './SignupCardButton';
import { useSignupPageContext } from '../../../../pages/SignupContext';
import { SignupEventName } from '../../../../utils/SignupLogger';

function useHints() {
  const { docsLocale } = useSignupPageContext();

  const renderHint = useCallback((imageUrl: string, text: React.ReactElement) => {
    return (
      <Row columnGap="16px" align="center">
        <Column height="48px" width="48px" align="center" verticalAlign="middle">
          <img src={imageUrl} />
        </Column>
        <Column flexGrow="1" verticalAlign="middle">
          <Text fontWeight="600" lineHeight="150%">
            {text}
          </Text>
        </Column>
      </Row>
    );
  }, []);

  return useMemo(() => {
    return (
      <>
        {renderHint(
          '/static/images/snowflake-document.svg',
          <FormattedMessage
            id="Get started with <doc>Snowflake documentation</doc>"
            values={{
              // eslint-disable-next-line
              doc: (...chunks: any[]) => {
                return (
                  <TextLink
                    href={`https://docs.snowflake.com/${docsLocale.value}/`}
                    color={Color.Blue50}
                    target="_blank"
                    fontWeight="600"
                  >
                    {chunks}
                  </TextLink>
                );
              },
            }}
          />,
        )}
        {renderHint(
          '/static/images/virtual-onboarding.svg',
          <FormattedMessage
            id="Sign up for a free, instructor-led <lab>Virtual Hands-On Lab</lab>"
            values={{
              // eslint-disable-next-line
              lab: (...chunks: any[]) => {
                return (
                  <TextLink
                    href="https://www.snowflake.com/virtual-hands-on-lab/?utm_cta=self-service-trial-thank-you-vhol"
                    color={Color.Blue50}
                    target="_blank"
                    fontWeight="600"
                  >
                    {chunks}
                  </TextLink>
                );
              },
            }}
          />,
        )}
        {renderHint(
          '/static/images/solution-center.svg',
          <FormattedMessage
            id="Explore industry-specific user cases and walkthroughs in our <sol>Solutions Center</sol>"
            values={{
              // eslint-disable-next-line
              sol: (...chunks: any[]) => {
                return (
                  <TextLink
                    href="https://developers.snowflake.com/solutions/"
                    color={Color.Blue50}
                    target="_blank"
                    fontWeight="600"
                  >
                    {chunks}
                  </TextLink>
                );
              },
            }}
          />,
        )}
      </>
    );
  }, [renderHint, docsLocale?.value]);
}

interface SignupCompleteCardProps {
  onTryAgain: () => void;
}

export const SignupCompleteCard = ({ onTryAgain }: SignupCompleteCardProps) => {
  const hints = useHints();
  const { accountCreationFinished, creationErrorCode } = useSignupFormController();
  const { logAction } = useTelemetry();
  const { modifiedSignupFlowExperimentGroup, renderingOptions } = useSignupPageContext();
  const [opacity, setOpacity] = React.useState(1);
  const [hasLoggedResult, setHasLoggedResult] = React.useState(false);

  // Track previous accountCreationFinished state to detect transition
  const prevFinishedRef = React.useRef(accountCreationFinished);

  // Trigger a smooth transition when the account creation finishes
  React.useEffect(() => {
    if (!prevFinishedRef.current && accountCreationFinished) {
      setOpacity(0);
      setTimeout(() => setOpacity(1), 50);
    }
    prevFinishedRef.current = accountCreationFinished;
  }, [accountCreationFinished]);

  // Log when success/failure card is shown to user (UI telemetry)
  React.useEffect(() => {
    if (accountCreationFinished && !hasLoggedResult) {
      const isModifiedFlow = modifiedSignupFlowExperimentGroup === 'MODIFIED_FLOW_TREATMENT';
      // Only log for modified flow
      if (isModifiedFlow) {
        if (creationErrorCode) {
          // Log failure card shown
          logAction(
            SignupEventName.SIGNUP_MODIFIED_FAILURE_CARD_SHOWN,
            'ui_click',
            'SignupCompleteCard',
            {
              errorCode: creationErrorCode,
              flowType: 'modified',
            },
          );
        } else {
          // Log success card shown
          logAction(
            SignupEventName.SIGNUP_MODIFIED_SUCCESS_CARD_SHOWN,
            'ui_click',
            'SignupCompleteCard',
            {
              flowType: 'modified',
            },
          );
        }
        setHasLoggedResult(true);
      }
    }
  }, [accountCreationFinished, creationErrorCode, hasLoggedResult, logAction]);

  const renderResources = () => {
    return (
      <>
        <Row fontSize="14px" align="center">
          <FormattedMessage id="Meanwhile, here are a few resources to check out:" />
        </Row>
        {hints}
      </>
    );
  };

  const renderTryAgainButton = () => {
    return (
      <SignupCardButton width="100%" onClick={onTryAgain}>
        <FormattedMessage id="Try again" />
      </SignupCardButton>
    );
  };

  return (
    <CardTemplate
      cardProps={{
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingBottom: '24px',
        fullbleed: true,
      }}
    >
      <div
        style={{
          opacity,
          transition: 'opacity 0.25s ease-in-out',
        }}
      >
        <Column rowGap="24px" width="100%">
          <Row align="center">
            <SignupCompleteHeader />
          </Row>
          {/* Only render resources/button when account creation is finished to avoid layout shift */}
          {accountCreationFinished &&
            !creationErrorCode &&
            !renderingOptions.skipRenderResources &&
            renderResources()}
          {accountCreationFinished && creationErrorCode && renderTryAgainButton()}
        </Column>
      </div>
    </CardTemplate>
  );
};
